/**
 * Onboarding tour step definitions per page and brand.
 *
 * Each step uses data-tour attributes as CSS selectors for targeting elements.
 * Steps are filtered at runtime based on brand config and enabled modules.
 */

export interface TourStep {
  /** CSS selector for the target element (using data-tour attributes) */
  target: string;
  /** Text content displayed in the tooltip */
  content: string;
  /** Optional placement hint */
  placement?: "top" | "bottom" | "left" | "right" | "auto";
  /** Disable beacon (flashing dot) for this step */
  disableBeacon?: boolean;
}

export interface TourPageConfig {
  pageKey: string;
  version: number;
}

/**
 * Tour page definitions with version numbers.
 * Increment version to force re-show tour after step changes.
 */
export const TOUR_PAGES: Record<string, TourPageConfig> = {
  dashboard: {
    pageKey: "dashboard",
    version: 1,
  },
  "grants-calls": {
    pageKey: "grants-calls",
    version: 2,
  },
};

interface DashboardTourOptions {
  /** Whether user has grants:calls module enabled */
  hasGrantsCalls: boolean;
  /** Docs URL from domain config (undefined for brands without docs) */
  docsUrl: string | undefined;
  /** Whether current viewport is mobile (< lg breakpoint) */
  isMobile: boolean;
}

/**
 * Get tour steps for the Dashboard page.
 *
 * Steps:
 * 1. Sidebar (or hamburger on mobile) - always
 * 2. "Nabory" parent menu item - only when hasGrantsCalls (targets always-visible parent,
 *    not collapsed submenu items that may not be in DOM)
 * 3. "Pomoc" link - always (content varies based on docsUrl)
 */
export function getDashboardTourSteps(options: DashboardTourOptions): TourStep[] {
  const { hasGrantsCalls, docsUrl, isMobile } = options;

  const steps: TourStep[] = [];

  // Step 1: Sidebar / Mobile menu
  steps.push({
    target: isMobile
      ? '[data-tour="mobile-menu-btn"]'
      : '[data-tour="sidebar"]',
    content: "Tu znajdziesz wszystkie sekcje serwisu",
    placement: isMobile ? "bottom" : "right",
    disableBeacon: true,
  });

  // Step 2: Nabory parent menu item (conditional)
  // Targets the always-visible parent item instead of collapsed submenu items
  // that may not be in DOM when the Nabory submenu is not expanded.
  if (hasGrantsCalls) {
    steps.push({
      target: '[data-tour="sidebar-grants"]',
      content: "Tutaj przeglądasz aktywne nabory z dotacjami i ustawiasz alerty o nowych naborach",
      placement: "right",
      disableBeacon: true,
    });
  }

  // Step 3: Help link (content depends on docsUrl)
  steps.push({
    target: '[data-tour="sidebar-help"]',
    content: docsUrl
      ? "Masz pytania? Sprawdź bazę wiedzy lub napisz do nas. Samouczek możesz ponownie uruchomić w zakładce Pomoc."
      : "Masz pytania? Napisz do nas. Samouczek możesz ponownie uruchomić w zakładce Pomoc.",
    placement: "right",
    disableBeacon: true,
  });

  return steps;
}

/**
 * Get tour steps for the Grants Calls page (/grants/calls).
 *
 * Steps:
 * 1. Advanced filters button
 * 2. Save alert button
 * 3. Alerts in sidebar
 * 4. Saved views bar
 * 5. Help / knowledge base in sidebar
 */
export function getGrantsCallsTourSteps(): TourStep[] {
  return [
    {
      target: '[data-tour="calls-advanced-filters"]',
      content: "Tutaj znajdziesz dodatkowe filtry: operator, grupa docelowa, kwota",
      placement: "bottom",
      disableBeacon: true,
    },
    {
      target: '[data-tour="calls-save-alert"]',
      content: "Po ustawieniu filtrów możesz zapisać alert — dostaniesz email gdy pojawi się nabór pasujący do Twoich kryteriów",
      placement: "bottom",
      disableBeacon: true,
    },
    {
      target: '[data-tour="sidebar-alerts"]',
      content: "Twoje zapisane alerty znajdziesz tutaj",
      placement: "right",
      disableBeacon: true,
    },
    {
      target: '[data-tour="calls-saved-views"]',
      content: "Zapisz ulubione zestawy filtrów jako widoki, żeby szybko do nich wracać",
      placement: "bottom",
      disableBeacon: true,
    },
    {
      target: '[data-tour="sidebar-help"]',
      content: "W zakładce Pomoc znajdziesz bazę wiedzy z instrukcjami jak korzystać z serwisu. Samouczek możesz ponownie uruchomić tutaj.",
      placement: "right",
      disableBeacon: true,
    },
  ];
}

/**
 * Storage key prefix for use in Help page "reset all tours" button.
 */
export const TOUR_STORAGE_KEY_PREFIX = "onboarding-tour-";
